import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IWidgetAttachments, widgetHttpHeader } from 'scw-digital-boards-widgets';
import { ELoadStatus } from '../../../state/state.interface';
import { HttpUtilitiesService, IGetManyResponse } from '../../../utilities/http-utilities.service';

export interface ImageUploadState {
  deleteStatus: ELoadStatus;
  errorStatus: number | null;
  previewAttachment: IWidgetAttachments | null;
  uploadedAttachments: IWidgetAttachments[] | null;
  uploadStatus: ELoadStatus;
}

@Injectable()
export class ImageUploadStore extends ComponentStore<ImageUploadState> {
  public readonly uploadedAttachments$: Observable<IWidgetAttachments[] | null> = this.select(
    (state) => state.uploadedAttachments,
  );
  public readonly uploadStatus$: Observable<ELoadStatus> = this.select((state) => state.uploadStatus);
  public readonly errorStatus$: Observable<number | null> = this.select((state) => state.errorStatus);
  public readonly previewAttachment$: Observable<IWidgetAttachments | null> = this.select(
    (state) => state.previewAttachment,
  );

  public readonly loadUploadedImages = this.effect<number[]>((trigger$) => {
    return trigger$.pipe(
      filter((ids) => !!ids && ids.length > 0),
      switchMap((ids) => {
        const widgetAttachmentsParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
          filters: [
            {
              field: 'id',
              ids,
            },
          ],
          limit: 100,
        });

        this.patchState({ errorStatus: null, uploadStatus: ELoadStatus.loading });

        return this.getAttachments({
          headers: widgetHttpHeader,
          params: widgetAttachmentsParams.set('thumbnail', 'true'),
        }).pipe(
          tapResponse(
            (widgetAttachments) => {
              this.patchState({
                uploadedAttachments: widgetAttachments.data,
                uploadStatus: ELoadStatus.success,
              });
            },
            (error: { status: number }) => {
              this.patchState({ errorStatus: error.status, uploadStatus: ELoadStatus.failure });
            },
          ),
          catchError((error: { status: number }) => {
            this.patchState({ errorStatus: error.status, uploadStatus: ELoadStatus.failure });

            return EMPTY;
          }),
        );
      }),
    );
  });

  public readonly loadPreviewImage = this.effect<number>((id$) => {
    return id$.pipe(
      switchMap((id) => {
        const previewParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters({
          filters: [
            {
              field: 'id',
              ids: [id],
            },
          ],
          limit: 1,
        });

        this.patchState({ errorStatus: null });

        return this.getAttachments({ headers: widgetHttpHeader, params: previewParams }).pipe(
          tapResponse(
            (widgetAttachments) => {
              this.patchState({ previewAttachment: widgetAttachments.data?.[0] ?? null });
            },
            (error: { status: number }) => {
              this.patchState({ errorStatus: error.status });
            },
          ),
          catchError((error: { status: number }) => {
            this.patchState({ errorStatus: error.status });

            return EMPTY;
          }),
        );
      }),
    );
  });

  public readonly clearPreviewImage = this.updater(
    (state): ImageUploadState => ({ ...state, previewAttachment: null }),
  );

  public readonly deleteAttachment = this.effect<number>((id$) => {
    return id$.pipe(
      switchMap((id) => {
        this.patchState({ deleteStatus: ELoadStatus.loading, errorStatus: null });

        return this.deleteAttachmentRequest(id, { headers: widgetHttpHeader }).pipe(
          tapResponse(
            () => {
              this.patchState({ deleteStatus: ELoadStatus.success });
            },
            (error: { status: number }) => {
              this.patchState({ deleteStatus: ELoadStatus.failure, errorStatus: error.status });
            },
          ),
          catchError((error: { status: number }) => {
            this.patchState({ deleteStatus: ELoadStatus.failure, errorStatus: error.status });

            return EMPTY;
          }),
        );
      }),
    );
  });

  constructor(
    private readonly httpUtilities: HttpUtilitiesService,
    private http: HttpClient,
  ) {
    super({
      deleteStatus: ELoadStatus.initial,
      errorStatus: null,
      previewAttachment: null,
      uploadedAttachments: null,
      uploadStatus: ELoadStatus.initial,
    });
  }

  public getAttachments(options: Parameters<HttpClient['get']>[1]): Observable<IGetManyResponse<IWidgetAttachments>> {
    return this.http.get<IGetManyResponse<IWidgetAttachments>>('widget-attachments', {
      ...options,
    });
  }

  public deleteAttachmentRequest(id: number, options: Parameters<HttpClient['delete']>[1]): Observable<void> {
    return this.http.delete<void>(`widget-attachments/${id}`, {
      ...options,
    });
  }
}
